// ---------------------------------------------------------------------------
// DW.2: Agent Approval Card — Human-in-the-Loop UI
// ---------------------------------------------------------------------------
// Level 0: ALL agent actions need human approval before execution.
// EU AI Act: aiGenerated flag always visible. User can edit, approve, or reject.
// ---------------------------------------------------------------------------

import { useState, useCallback } from "react";
import { Bot, Check, X, Edit3, ChevronDown, ChevronUp, Shield, AlertTriangle } from "lucide-react";
import { Skeleton } from "../skeleton";

// ---------------------------------------------------------------------------
// Types (mirrors packages/ai/src/agents/types.ts, no import — frontend copy)
// ---------------------------------------------------------------------------

interface ProposedAction {
  toolName: string;
  arguments: Record<string, unknown>;
  reasoning: string;
  confidence: number;
  aiGenerated: true;
}

interface AgentApprovalProps {
  taskId: string;
  agentName: string;
  agentType: string;
  proposedAction: ProposedAction;
  onApprove: (taskId: string, modifiedArgs?: Record<string, unknown>) => void;
  onReject: (taskId: string, reason: string) => void;
  isExecuting?: boolean;
}

// ---------------------------------------------------------------------------
// Confidence display
// ---------------------------------------------------------------------------

function getConfidenceColor(confidence: number): string {
  if (confidence >= 0.8) return "var(--color-success)";
  if (confidence >= 0.6) return "var(--color-warning)";
  return "var(--color-destructive)";
}

function getConfidenceLabel(confidence: number): string {
  if (confidence >= 0.8) return "High";
  if (confidence >= 0.6) return "Medium";
  return "Low";
}

// ---------------------------------------------------------------------------
// Component
// ---------------------------------------------------------------------------

export function AgentApprovalCard({
  taskId,
  agentName,
  agentType,
  proposedAction,
  onApprove,
  onReject,
  isExecuting = false,
}: AgentApprovalProps) {
  const [isEditing, setIsEditing] = useState(false);
  const [editedArgs, setEditedArgs] = useState(JSON.stringify(proposedAction.arguments, null, 2));
  const [isArgsExpanded, setIsArgsExpanded] = useState(false);
  const [rejectReason, setRejectReason] = useState("");
  const [showRejectInput, setShowRejectInput] = useState(false);
  const [parseError, setParseError] = useState<string | null>(null);

  const handleApprove = useCallback(() => {
    if (isEditing) {
      try {
        const parsed = JSON.parse(editedArgs);
        setParseError(null);
        onApprove(taskId, parsed);
      } catch {
        setParseError("Invalid JSON — please fix before approving");
        return;
      }
    } else {
      onApprove(taskId);
    }
  }, [isEditing, editedArgs, taskId, onApprove]);

  const handleReject = useCallback(() => {
    onReject(taskId, rejectReason || "User rejected without reason");
    setShowRejectInput(false);
    setRejectReason("");
  }, [taskId, rejectReason, onReject]);

  return (
    <div
      className="border rounded-lg overflow-hidden"
      style={{
        borderColor: "var(--color-border)",
        backgroundColor: "var(--color-card)",
      }}
    >
      {/* Header */}
      <div
        className="flex items-center gap-3 px-4 py-3"
        style={{ borderBottom: "1px solid var(--color-border)" }}
      >
        <div
          className="flex items-center justify-center w-8 h-8 rounded-full"
          style={{ backgroundColor: "var(--color-primary-light, rgba(16, 64, 204, 0.1))" }}
        >
          <Bot size={16} style={{ color: "var(--color-primary)" }} />
        </div>
        <div className="flex-1 min-w-0">
          <div className="flex items-center gap-2">
            <span className="font-medium text-sm" style={{ color: "var(--color-foreground)" }}>
              {agentName}
            </span>
            <span
              className="px-2 py-0.5 text-xs rounded-full"
              style={{
                backgroundColor: "var(--color-muted)",
                color: "var(--color-muted-foreground)",
              }}
            >
              {agentType.replace(/_/g, " ")}
            </span>
          </div>
          <div className="flex items-center gap-1.5 mt-0.5">
            <Shield size={12} style={{ color: "var(--color-muted-foreground)" }} />
            <span className="text-xs" style={{ color: "var(--color-muted-foreground)" }}>
              AI-generated action — requires your approval
            </span>
          </div>
        </div>
        <div className="flex items-center gap-1.5">
          <div
            className="w-2 h-2 rounded-full"
            style={{ backgroundColor: getConfidenceColor(proposedAction.confidence) }}
          />
          <span className="text-xs font-medium" style={{ color: getConfidenceColor(proposedAction.confidence) }}>
            {getConfidenceLabel(proposedAction.confidence)} ({Math.round(proposedAction.confidence * 100)}%)
          </span>
        </div>
      </div>

      {/* Action details */}
      <div className="px-4 py-3 space-y-3">
        {/* Tool name */}
        <div className="flex items-center gap-2">
          <span className="text-xs font-medium" style={{ color: "var(--color-muted-foreground)" }}>
            Action:
          </span>
          <code
            className="px-2 py-0.5 text-xs rounded"
            style={{
              backgroundColor: "var(--color-muted)",
              color: "var(--color-foreground)",
              fontFamily: "var(--font-mono, monospace)",
            }}
          >
            {proposedAction.toolName}
          </code>
        </div>

        {/* Reasoning */}
        <div>
          <span className="text-xs font-medium" style={{ color: "var(--color-muted-foreground)" }}>
            Reasoning:
          </span>
          <p className="mt-1 text-sm" style={{ color: "var(--color-foreground)" }}>
            {proposedAction.reasoning}
          </p>
        </div>

        {/* Arguments (collapsible) */}
        <div>
          <button
            type="button"
            className="flex items-center gap-1 text-xs font-medium cursor-pointer"
            style={{ color: "var(--color-muted-foreground)", background: "none", border: "none", padding: 0 }}
            onClick={() => setIsArgsExpanded(!isArgsExpanded)}
          >
            {isArgsExpanded ? <ChevronUp size={14} /> : <ChevronDown size={14} />}
            Arguments
          </button>
          {isArgsExpanded && (
            <div className="mt-2">
              {isEditing ? (
                <div>
                  <textarea
                    value={editedArgs}
                    onChange={(e) => {
                      setEditedArgs(e.target.value);
                      setParseError(null);
                    }}
                    className="w-full p-2 text-xs rounded border"
                    style={{
                      fontFamily: "var(--font-mono, monospace)",
                      backgroundColor: "var(--color-background)",
                      borderColor: parseError ? "var(--color-destructive)" : "var(--color-border)",
                      color: "var(--color-foreground)",
                      minHeight: "80px",
                      resize: "vertical",
                    }}
                  />
                  {parseError && (
                    <div className="flex items-center gap-1 mt-1">
                      <AlertTriangle size={12} style={{ color: "var(--color-destructive)" }} />
                      <span className="text-xs" style={{ color: "var(--color-destructive)" }}>{parseError}</span>
                    </div>
                  )}
                </div>
              ) : (
                <pre
                  className="p-2 text-xs rounded overflow-x-auto"
                  style={{
                    backgroundColor: "var(--color-muted)",
                    color: "var(--color-foreground)",
                    fontFamily: "var(--font-mono, monospace)",
                  }}
                >
                  {JSON.stringify(proposedAction.arguments, null, 2)}
                </pre>
              )}
            </div>
          )}
        </div>

        {/* Rejection reason input */}
        {showRejectInput && (
          <div className="space-y-2">
            <input
              type="text"
              value={rejectReason}
              onChange={(e) => setRejectReason(e.target.value)}
              placeholder="Why are you rejecting this action? (optional)"
              className="w-full px-3 py-2 text-sm rounded border"
              style={{
                backgroundColor: "var(--color-background)",
                borderColor: "var(--color-border)",
                color: "var(--color-foreground)",
              }}
            />
            <div className="flex gap-2">
              <button
                type="button"
                onClick={handleReject}
                className="px-3 py-1.5 text-xs font-medium rounded"
                style={{
                  backgroundColor: "var(--color-destructive)",
                  color: "white",
                  border: "none",
                  cursor: "pointer",
                }}
              >
                Confirm Reject
              </button>
              <button
                type="button"
                onClick={() => { setShowRejectInput(false); setRejectReason(""); }}
                className="px-3 py-1.5 text-xs font-medium rounded"
                style={{
                  backgroundColor: "var(--color-muted)",
                  color: "var(--color-muted-foreground)",
                  border: "none",
                  cursor: "pointer",
                }}
              >
                Cancel
              </button>
            </div>
          </div>
        )}
      </div>

      {/* Action buttons */}
      <div
        className="flex items-center gap-2 px-4 py-3"
        style={{ borderTop: "1px solid var(--color-border)", backgroundColor: "var(--color-muted)" }}
      >
        <button
          type="button"
          onClick={handleApprove}
          disabled={isExecuting}
          className="flex items-center gap-1.5 px-4 py-2 text-sm font-medium rounded-md"
          style={{
            backgroundColor: isExecuting ? "var(--color-muted)" : "var(--color-primary)",
            color: isExecuting ? "var(--color-muted-foreground)" : "white",
            border: "none",
            cursor: isExecuting ? "not-allowed" : "pointer",
            opacity: isExecuting ? 0.6 : 1,
          }}
        >
          {isExecuting ? (
            <>
              <Skeleton className="h-3.5 w-3.5" />
              <Skeleton className="h-3.5 w-16" />
            </>
          ) : (
            <>
              <Check size={14} />
              {isEditing ? "Approve (edited)" : "Approve"}
            </>
          )}
        </button>

        <button
          type="button"
          onClick={() => setIsEditing(!isEditing)}
          disabled={isExecuting}
          className="flex items-center gap-1.5 px-3 py-2 text-sm font-medium rounded-md"
          style={{
            backgroundColor: isEditing ? "var(--color-accent)" : "transparent",
            color: "var(--color-foreground)",
            border: "1px solid var(--color-border)",
            cursor: isExecuting ? "not-allowed" : "pointer",
          }}
        >
          <Edit3 size={14} />
          {isEditing ? "Editing" : "Edit"}
        </button>

        {!showRejectInput && (
          <button
            type="button"
            onClick={() => setShowRejectInput(true)}
            disabled={isExecuting}
            className="flex items-center gap-1.5 px-3 py-2 text-sm font-medium rounded-md ml-auto"
            style={{
              backgroundColor: "transparent",
              color: "var(--color-destructive)",
              border: "1px solid var(--color-destructive)",
              cursor: isExecuting ? "not-allowed" : "pointer",
            }}
          >
            <X size={14} />
            Reject
          </button>
        )}
      </div>
    </div>
  );
}
